import { Autocomplete, Box, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";

// Hierarchical territory data + picker. Territories form a tree via
// platform.territory_parents; both admin_territories_view and territory_parents
// are readable by authenticated. useTerritoryTree returns a depth-ordered flat
// list (parents before children) so dropdowns + the admin listing can indent.

export interface TerritoryNode {
  id: string;
  name: string;
  parent_id: string | null;
  depth: number;
  is_active: boolean;
}

interface RawTerritory {
  id: string;
  name: string;
  is_active: boolean;
}

export interface TerritoryEdge {
  territory_id: string;
  parent_territory_id: string | null;
}

/**
 * Flatten the territory graph into a depth-ordered list (parents before
 * children, each territory listed once) for indented dropdowns.
 *
 * Territories support *overlap* — a territory may have more than one parent
 * (platform.territory_parents PK is (territory_id, parent_territory_id), and the
 * admin_save_territory RPC takes a parent_ids[] array). Earlier implementations
 * either kept a single parent per child (last edge wins → "flattens past two
 * levels") or walked every edge (→ duplicate rows at wrong depths when a deep
 * node also has a shallow/ancestor parent).
 *
 * Correct model: a territory's depth is its **longest** path from a root, i.e.
 * max(parentDepth) + 1. The shallow/ancestor parent never wins, so depth is
 * stable regardless of overlap or accidental transitive edges. Each territory
 * is shown once, nested under the parent that gives it that depth (its deepest
 * ancestor = its true direct parent). Cycle-safe; orphans/cycles fall back to
 * depth 0 so nothing vanishes.
 */
export function buildTerritoryTree(
  territories: RawTerritory[],
  edges: TerritoryEdge[],
): TerritoryNode[] {
  const idSet = new Set(territories.map((t) => t.id));
  const byId = new Map(territories.map((t) => [t.id, t]));

  // Distinct in-set parents per child.
  const parentsOf = new Map<string, string[]>();
  for (const e of edges) {
    if (!idSet.has(e.territory_id)) continue;
    const parent = e.parent_territory_id;
    if (parent == null || !idSet.has(parent)) continue;
    const arr = parentsOf.get(e.territory_id) ?? [];
    if (!arr.includes(parent)) arr.push(parent);
    parentsOf.set(e.territory_id, arr);
  }

  // depth = max(parent depth) + 1 (longest path from a root). Memoized,
  // cycle-safe (a back-edge to an ancestor on the current stack is ignored).
  const depthCache = new Map<string, number>();
  const computeDepth = (id: string, stack: Set<string>): number => {
    const cached = depthCache.get(id);
    if (cached != null) return cached;
    let best = 0;
    let hasUsableParent = false;
    for (const p of parentsOf.get(id) ?? []) {
      if (stack.has(p)) continue; // cycle guard
      hasUsableParent = true;
      const d = computeDepth(p, new Set(stack).add(id)) + 1;
      if (d > best) best = d;
    }
    const depth = hasUsableParent ? best : 0;
    depthCache.set(id, depth);
    return depth;
  };
  for (const t of territories) computeDepth(t.id, new Set());

  // Display parent = the parent achieving the max depth (the true direct parent).
  const displayParent = new Map<string, string | null>();
  for (const t of territories) {
    let chosen: string | null = null;
    let best = -1;
    for (const p of parentsOf.get(t.id) ?? []) {
      const d = depthCache.get(p) ?? 0;
      if (d > best) {
        best = d;
        chosen = p;
      }
    }
    displayParent.set(t.id, chosen);
  }

  const childrenOf = new Map<string | null, string[]>();
  for (const t of territories) {
    const dp = displayParent.get(t.id) ?? null;
    const arr = childrenOf.get(dp) ?? [];
    arr.push(t.id);
    childrenOf.set(dp, arr);
  }

  const byName = (a: string, b: string) => byId.get(a)!.name.localeCompare(byId.get(b)!.name);

  const out: TerritoryNode[] = [];
  const emitted = new Set<string>();
  const visit = (id: string, path: Set<string>) => {
    if (path.has(id) || emitted.has(id)) return;
    const t = byId.get(id)!;
    out.push({
      id,
      name: t.name,
      parent_id: displayParent.get(id) ?? null,
      depth: depthCache.get(id) ?? 0,
      is_active: t.is_active,
    });
    emitted.add(id);
    const nextPath = new Set(path).add(id);
    for (const child of (childrenOf.get(id) ?? []).slice().sort(byName)) {
      visit(child, nextPath);
    }
  };

  for (const r of (childrenOf.get(null) ?? []).slice().sort(byName)) visit(r, new Set());

  // Safety net: anything not reached (pure cycle) is still surfaced at depth 0
  // so it never silently vanishes from the picker.
  for (const t of [...territories].sort((a, b) => a.name.localeCompare(b.name))) {
    if (!emitted.has(t.id)) visit(t.id, new Set());
  }
  return out;
}

export function useTerritoryTree() {
  return useQuery({
    queryKey: ["territory_tree"],
    staleTime: 60_000,
    queryFn: async (): Promise<TerritoryNode[]> => {
      const [terrs, parents] = await Promise.all([
        supabase.from("admin_territories_view").select("id, name, is_active").order("name"),
        // territory_parents lives in the platform schema (not public) and isn't
        // in the generated types — cast for the read.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase.schema("platform") as any)
          .from("territory_parents")
          .select("territory_id, parent_territory_id"),
      ]);
      if (terrs.error) throw terrs.error;
      if (parents.error) throw parents.error;

      return buildTerritoryTree(
        (terrs.data ?? []) as RawTerritory[],
        (parents.data ?? []) as TerritoryEdge[],
      );
    },
  });
}

export interface TerritoryPickerProps {
  label?: string;
  value: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
  size?: "small" | "medium";
  error?: boolean;
  helperText?: string;
}

export function TerritoryPicker({
  label = "Territory",
  value,
  onChange,
  disabled,
  size = "small",
  error,
  helperText,
}: TerritoryPickerProps) {
  const { data: nodes = [], isLoading } = useTerritoryTree();
  const selected = nodes.find((n) => n.id === value) ?? null;

  return (
    <Autocomplete<TerritoryNode>
      options={nodes}
      loading={isLoading}
      value={selected}
      disabled={disabled}
      onChange={(_, v) => onChange(v?.id ?? null)}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderOption={(props, o, { index }) => {
        // Inline paddingLeft (not sx) so MUI's own option padding can't override
        // the per-depth indent — the depth must always read visually.
        const { style, ...rest } = props as React.HTMLAttributes<HTMLLIElement>;
        return (
          <Box
            component="li"
            {...rest}
            key={`${o.id}-${index}`}
            style={{ ...style, paddingLeft: 12 + o.depth * 18 }}
          >
            {o.depth > 0 && (
              <Box component="span" sx={{ color: "text.disabled", mr: 0.5 }}>
                └
              </Box>
            )}
            {o.name}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} label={label} error={error} helperText={helperText} />
      )}
      fullWidth
      size={size}
    />
  );
}
